import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import {
  Feature,
  useConferencingAuthorizeUrl,
  useDeleteAllIntegrations,
  useIntegrations,
  VideoCallProvider,
} from '@app/api';
import { Button, InformationBox, LockFeatureChip, useToast } from '@app/core';
import classNames from 'classnames';
import { SettingContent } from './components/SettingContent';
import { AddCalendarAccountButton } from './components/AddCalendarAccountButton';
import { IntegrationAccountCard } from './components/IntegrationAccountCard';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h2,
    marginBottom: theme.marginBase * 3,
  },
  section: {
    marginBottom: theme.marginBase * 8,
  },
  boxText: {
    marginBottom: theme.marginBase,
  },
  boxButtonLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.marginBase,
  },
  subTitle: {
    ...theme.fonts.h4,
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase,
  },
  disconnectAction: {
    marginTop: theme.marginBase,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  infoBox: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
  },
  buttonIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  buttonContent: {
    display: 'flex',
    gap: theme.marginBase,
  },
  buttons: {
    display: 'flex',
    gap: theme.marginBase,
  },
}));

export const ToolIntegrations = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const { data: integrations } = useIntegrations();

  const { data: authConferencingUrl } = useConferencingAuthorizeUrl(
    VideoCallProvider.ZOOM,
  );
  const { mutate: revokeAuthorization } = useDeleteAllIntegrations();

  if (!integrations?.length) {
    return (
      <SettingContent>
        <section className={classes.section}>
          <h2 className={classes.title}>
            {t('settings.toolIntegrations.calendar.title')}
          </h2>
          <InformationBox
            className={classes.infoBox}
            title={t('settings.toolIntegrations.calendar.noIntegrationTitle')}
          >
            <div className={classes.boxText}>
              {t('settings.toolIntegrations.calendar.noIntegrationText')}
            </div>
            <div className={classes.boxButtonLine}>
              <LockFeatureChip feature={Feature.CALENDAR_INTEGRATION} />
              <AddCalendarAccountButton>
                <div className={classes.buttonContent}>
                  <i
                    className={classNames(
                      'ri-add-circle-line',
                      classes.buttonIcon,
                    )}
                  />
                  {t('settings.toolIntegrations.calendar.addProfile')}
                </div>
              </AddCalendarAccountButton>
            </div>
          </InformationBox>
        </section>
      </SettingContent>
    );
  }

  return (
    <SettingContent>
      <section className={classes.section}>
        <h2 className={classes.title}>
          {t('settings.toolIntegrations.calendar.title')}
        </h2>

        <InformationBox
          className={classes.infoBox}
          title={t('settings.toolIntegrations.calendar.boxTitle')}
        >
          <div className={classes.boxText}>
            {t('settings.toolIntegrations.calendar.boxText')}
          </div>
          <div className={classes.disconnectAction}>
            <Button
              target="_blank"
              small
              variant="transparent"
              color="red"
              onClick={() =>
                revokeAuthorization(undefined, {
                  onSuccess: () =>
                    toast.success(
                      'settings.toolIntegrations.calendar.profile.deleted',
                    ),
                  onError: (e: Error) => toast.exception(e),
                })
              }
            >
              {t('settings.toolIntegrations.calendar.disconnectAccount')}
            </Button>
          </div>
        </InformationBox>

        <h3 className={classes.subTitle}>
          {t('settings.toolIntegrations.calendar.profiles')}
        </h3>

        {integrations?.map((account) => (
          <IntegrationAccountCard account={account} />
        ))}
        <div className={classes.buttons}>
          <AddCalendarAccountButton>
            <div className={classes.buttonContent}>
              <i
                className={classNames('ri-add-circle-line', classes.buttonIcon)}
              />
              {t('settings.toolIntegrations.calendar.addProfile')}
            </div>
          </AddCalendarAccountButton>

          {authConferencingUrl && (
            <Button
              text={t(
                'editSession.settings.moderator.conferencingProvider.addZoom',
              )}
              variant="transparent"
              color="blue"
              target="_blank"
              small
              href={authConferencingUrl.redirectUrl}
            >
              <i
                className={classNames('ri-add-circle-line', classes.buttonIcon)}
              />
            </Button>
          )}
        </div>
      </section>
    </SettingContent>
  );
};
